import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../../components/Layout";
import { api, API as API_BASE, TOKEN_KEY } from "../../lib/api";
import { ArrowLeft, Download, Printer, FileText, Filter } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * Full-page reconciliation report.
 *  - Default period = current calendar month.
 *  - Per item × site rows: opening → purchases → breakage → closing
 *  - Per-site totals + grand network total (transfers excluded from additions)
 *  - CSV export + print
 */
export default function ReconciliationReport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [site, setSite] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const canView = user?.role === "admin" || !!user?.permissions_effective?.view_inventory;

  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data?.sites || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/reconciliation", {
        params: { from, to, site: site || undefined },
      });
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  }, [from, to, site]);

  useEffect(() => { if (canView) load(); }, [canView, load]);

  const csvUrl = useMemo(() => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const u = new URL(`${API_BASE}/inventory/reconciliation.csv`);
      u.searchParams.set("from", from);
      u.searchParams.set("to", to);
      if (site) u.searchParams.set("site", site);
      if (token) u.searchParams.set("session", token); // tolerated; cookie is authoritative
      return u.toString();
    } catch { return "#"; }
  }, [from, to, site]);

  const downloadCsv = async () => {
    // axios returns Blob — saves with the response filename so it lands as .csv
    try {
      const resp = await api.get("/inventory/reconciliation.csv", {
        params: { from, to, site: site || undefined },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reconciliation_${from}_${to}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };

  if (!canView) {
    return <AppShell title="Reconciliation"><div className="p-10 text-center text-slate-500">No inventory access</div></AppShell>;
  }

  const rows = data?.rows || [];
  const siteTotals = data?.site_totals || {};
  const grand = data?.grand_total || null;

  return (
    <AppShell title="Inventory Reports">
      <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8 space-y-5" data-testid="recon-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            data-testid="recon-back"
            onClick={() => navigate("/inventory")}
            className="rounded-full px-3.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          >
            <ArrowLeft size={13} strokeWidth={2.2} /> Inventory home
          </button>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="recon-print"
              onClick={() => window.print()}
              className="rounded-full px-3.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
            >
              <Printer size={13} strokeWidth={2} /> Print
            </button>
            <button
              data-testid="recon-csv"
              onClick={downloadCsv}
              className="rounded-full px-3.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 bg-olive text-white hover:opacity-90"
            >
              <Download size={13} strokeWidth={2} /> CSV
            </button>
          </div>
        </div>

        {/* Title + filters */}
        <section className="bg-white border border-slate-200 rounded-2xl p-5 lg:p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-olive/10 grid place-items-center text-olive shrink-0">
              <FileText size={20} strokeWidth={1.8} />
            </div>
            <div className="flex-1">
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">CCG / Inventory</div>
              <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight mt-0.5">Reconciliation Report</h1>
              <p className="text-sm text-slate-500 mt-2">
                Opening → purchases → breakage → closing, per item per site. Inter-site transfers count as additions at the destination but are excluded from the network-level grand total.
              </p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="recon-filters">
            <Field label="From">
              <input data-testid="recon-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls}/>
            </Field>
            <Field label="To">
              <input data-testid="recon-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls}/>
            </Field>
            <Field label="Site">
              <select data-testid="recon-site" value={site} onChange={(e) => setSite(e.target.value)} className={inputCls}>
                <option value="">All sites</option>
                {sites.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <button
              data-testid="recon-apply"
              onClick={load}
              className="rounded-xl px-4 py-2 text-xs font-semibold inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white hover:bg-olive transition-colors h-[38px]"
            >
              <Filter size={12} strokeWidth={2.2} /> Apply
            </button>
          </div>
        </section>

        {/* Grand-total card (headline numbers) */}
        {grand && (
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="recon-grand">
            <StatTile testid="grand-replacement" tone="olive" label="Total replacement value" value={fmtINR(grand.purchases_value)} sub={`${grand.purchases_qty} units`} />
            <StatTile testid="grand-breakage" tone="red" label="Total breakage value" value={fmtINR(grand.breakage_value)} sub={`${grand.breakage_qty} units`} />
            <StatTile testid="grand-opening" tone="slate" label="Opening (qty)" value={grand.opening_qty} />
            <StatTile testid="grand-closing" tone="slate" label="Closing (qty)" value={grand.closing_qty} />
          </section>
        )}

        {/* Per-row table grouped by site */}
        <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="recon-table-wrap">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center" data-testid="recon-empty">
              <FileText className="mx-auto text-slate-300" size={28} strokeWidth={1.5} />
              <div className="text-sm font-medium text-slate-900 mt-2">No data in this period</div>
              <p className="text-xs text-slate-500 mt-1">Adjust the date range, or record stock movements to populate the report.</p>
            </div>
          ) : (
            <GroupedTable rows={rows} siteTotals={siteTotals} />
          )}
        </section>

        <p className="text-[10px] text-slate-400 text-center print:hidden">
          Generated {new Date().toLocaleString()} · Past values are pinned to the rate in effect on the movement date.
        </p>
      </div>
    </AppShell>
  );
}

// ────────────────────── grouped table ──────────────────────
function GroupedTable({ rows, siteTotals }) {
  // group by site, preserve sort order
  const groups = {};
  for (const r of rows) {
    (groups[r.site] ||= []).push(r);
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50/60">
            <th className="text-left px-4 py-2.5">Item</th>
            <th className="text-left px-4 py-2.5 hidden md:table-cell">Category</th>
            <th className="text-right px-3 py-2.5">Opening</th>
            <th className="text-right px-3 py-2.5">Purchases</th>
            <th className="text-right px-3 py-2.5">Purch. Value</th>
            <th className="text-right px-3 py-2.5">Breakage</th>
            <th className="text-right px-3 py-2.5">Breakage Value</th>
            <th className="text-right px-3 py-2.5">Closing</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {Object.entries(groups).map(([site, gRows]) => (
            <React.Fragment key={site}>
              <tr className="bg-olive/5">
                <td colSpan={8} className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-olive">
                  {site}
                </td>
              </tr>
              {gRows.map((r, i) => (
                <tr key={`${site}-${r.item_id}-${i}`} data-testid={`recon-row-${site}-${r.item_id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-900 truncate">{r.item_name}</div>
                    <div className="text-[10px] text-slate-400 md:hidden">{r.category}</div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 hidden md:table-cell">{r.category}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{r.opening_qty}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{r.purchases_qty}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-olive">{fmtINR(r.purchases_value)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{r.breakage_qty}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-red-700">{fmtINR(r.breakage_value)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{r.closing_qty}</td>
                </tr>
              ))}
              {siteTotals[site] && (
                <tr className="bg-slate-50 font-semibold" data-testid={`recon-site-total-${site}`}>
                  <td className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-slate-700">Site total</td>
                  <td className="hidden md:table-cell"></td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{siteTotals[site].opening_qty}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{siteTotals[site].purchases_qty}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-olive">{fmtINR(siteTotals[site].purchases_value)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{siteTotals[site].breakage_qty}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-red-700">{fmtINR(siteTotals[site].breakage_value)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{siteTotals[site].closing_qty}</td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────── helpers + atoms ──────────────────────
const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30";
const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </label>
);
const StatTile = ({ testid, label, value, sub, tone = "slate" }) => {
  const t = {
    olive: "bg-olive/10 text-olive border-olive/30",
    red: "bg-red-50 text-red-700 border-red-200",
    slate: "bg-white text-slate-700 border-slate-200",
  }[tone] || "bg-white text-slate-700 border-slate-200";
  return (
    <div data-testid={testid} className={"rounded-2xl border px-4 py-3.5 " + t}>
      <div className="text-[11px] font-medium uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-semibold tracking-tight mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
};

function fmtINR(n) {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function monthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
